import _ from 'lodash';

import LogRocket from 'shared/logrocket';
import { identifyLDUser } from 'shared/observability/ld-identify';
import { flattenedIntegrationsForSegment } from './integrations';
import { isSuperAdmin } from './users';

export const buildSuperAdminTraits = () => ({
  name: 'dutchie',
  tags: 'dispensary_user_segment',
});

export const buildIntegrationsTraits = (dispoIntegrations) => {
  const allIntegrations = flattenedIntegrationsForSegment();
  _.forEach(dispoIntegrations, (integration) => {
    allIntegrations[integration.adapter] = integration.live;
  });
  return allIntegrations;
};

export const buildDispensaryTraits = (dispensary) => ({
  chain_id: dispensary?.chain,
  city: dispensary?.location?.city,
  country: dispensary?.location?.country,
  embedded: !_.isEmpty(dispensary?.embeddedMenuUrl),
  id: dispensary?.id,
  name: dispensary?.name,
  phone: dispensary?.phone,
  postalCode: dispensary?.location?.zipcode,
  state: dispensary?.location?.state,
  subdomain: !_.isEmpty(dispensary?.customDomainSettings),
  status: dispensary?.status,
  tags: 'dispensary_user_segment', // these tags are needed for Zendesk downstream
  tier: dispensary?.tier,
  timezone: dispensary?.timezone,
});

export const buildTraitsFromUser = (user) => {
  const traits = {
    createdAt: user.createdAt,
    email: _.get(user, 'emails[0].address'),
    firstName: user.profile.firstName,
    lastName: user.profile.lastName,
    name: user.profile.fullName,
    phone: user.profile.phone,
  };

  // flatten permissions
  _.merge(traits, user.profile.permissions);

  return traits;
};

function getEnvIdentifier(id) {
  return window.reactEnv.appEnv === 'production' ? id : 'developmentId';
}

export const segmentIdentifyAndGroup = (user, dispensary, integrations) => {
  if (!window.analytics || !user) {
    return;
  }
  const userId = getEnvIdentifier(user._id);
  // in all cases at least call identify on the user
  window.analytics.identify(userId, { ...buildTraitsFromUser(user) });
  // if user is super admin, we want to add them to the dutchie org in zendesk
  if (isSuperAdmin(user)) {
    window.analytics.group('superAdminId', { ...buildSuperAdminTraits() });
  }
  // when dispensary data is provided, we can group it into an org downstream
  if (dispensary) {
    let traits = buildDispensaryTraits(dispensary);
    const dispoId = getEnvIdentifier(dispensary.id);
    if (integrations) {
      traits = { ...traits, ...buildIntegrationsTraits(integrations) };
    }
    window.analytics.group(dispoId, traits);
  }
};

export const configureLogRocket = (user) => {
  if (!user) {
    return;
  }

  const traits = buildTraitsFromUser(user);
  LogRocket.identify(user._id, traits);
  identifyLDUser(user._id, traits);
};

export const openZendeskWidget = () => {
  if (_.isFunction(window.zE)) {
    try {
      window.zE('webWidget', 'open');
    } catch {
      window.zE('messenger', 'open');
    }
  }
};

export const showZendeskWidget = () => {
  if (_.isFunction(window.zE)) {
    try {
      window.zE('webWidget', 'show');
    } catch {
      window.zE('messenger', 'open');
    }
  }
};

export const hideZendeskWidget = () => {
  if (_.isFunction(window.zE)) {
    try {
      window.zE('webWidget', 'hide');
    } catch {
      window.zE('messenger', 'close');
    }
  }
};

export const closeZendeskWidget = () => {
  if (_.isFunction(window.zE)) {
    hideZendeskWidget();
    // run this no matter what but also it can throw exceptions
    try {
      window.zE('messenger', 'close');
    } catch {
      // no-op — messenger may already be closed
    }
  }
};

export function hideDecagonWidget() {
  if (!window.duet) {
    return;
  }
  window.duet.hide();
}
export function showDecagonWidget() {
  if (!window.duet) {
    return;
  }
  window.duet.unhide();
}
export function openDecagonWidget() {
  if (!window.duet) {
    return;
  }
  window.duet.unhide();
  window.duet.open();
}
export function closeDecagonWidget() {
  if (!window.duet) {
    return;
  }
  window.duet.close();
}

export function openChatWidget() {
  openZendeskWidget();
  openDecagonWidget();
}
